/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { ColorOptionDto2 } from '@/api-sdk';
import { iconCheckmark, iconChevron } from '@/components/icons';
import { ConfiguratorContextType, configuratorGlobal } from '@/context';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, buttonStyles, collapsibleOptionsStyles } from '@/styles';
import { getParamsObjectFromQueryString } from '@/utils';

import '@/components/image/fg-image';

@customElement('fg-collapsible-color')
export class FgColorCollapsible extends LitElement {
  @property({ type: Number })
  stepIndex!: number;

  @property({ type: String })
  title = '';

  @property({ type: Number })
  colorItemIndex!: number;

  @property({ type: Array })
  colors!: ColorOptionDto2[];

  @property({ type: Boolean })
  open?: boolean;

  @state()
  showAllColors = false;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  private static readonly COLOR_DISPLAY_THRESHOLD = 5;

  willUpdate(_changedProperties: PropertyValues) {
    if (!this.colors || !Array.isArray(this.colors)) {
      return;
    }

    if (_changedProperties.has('colors')) {
      const shouldShowAll =
        (this.colors.length >= FgColorCollapsible.COLOR_DISPLAY_THRESHOLD &&
          this.colors
            .slice(0, FgColorCollapsible.COLOR_DISPLAY_THRESHOLD)
            .every(item => item.queryString === null)) ||
        (this.colors.length > FgColorCollapsible.COLOR_DISPLAY_THRESHOLD &&
          this.colors
            .slice(FgColorCollapsible.COLOR_DISPLAY_THRESHOLD)
            .some(item => item.selected));
      if (shouldShowAll) {
        this.showAllColors = true;
      }

      // is there a preselected color?
      const { color } = this.configuratorContext;
      const selectedProductColor = this.colors.find(option => {
        const qsColor =
          (getParamsObjectFromQueryString(option.queryString as string)
            .colors as string[]) ?? [];
        return qsColor.includes(color ?? '') && qsColor.length === 1;
      });
      if (
        selectedProductColor &&
        !this.configuratorContext.firstGetConfiguratorCall
      ) {
        this.updateComponent(selectedProductColor.queryString);
        this.selectedColorImage(true, selectedProductColor);

        this.dispatchEvent(
          new Event('default-color-selected', { composed: true }),
        );
        return;
      }
      this.selectedColorImage(false);
    }
  }

  updateComponent(queryString: string | null | undefined) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  private getVisibleItemsCount(): number {
    if (!this.colors) {
      return 0;
    }

    if (
      this.showAllColors ||
      this.colors.length <= FgColorCollapsible.COLOR_DISPLAY_THRESHOLD + 1
    ) {
      return this.colors.filter(color => !color.disabled).length;
    } else {
      return this.colors
        .slice(0, FgColorCollapsible.COLOR_DISPLAY_THRESHOLD + 1)
        .filter(
          (color, index) =>
            !color.disabled &&
            index !== FgColorCollapsible.COLOR_DISPLAY_THRESHOLD,
        ).length;
    }
  }

  selectedColorImage(colorExists: boolean, options?: ColorOptionDto2) {
    let imageObject: {
      thumbImage: string | null | undefined;
      image: string | null | undefined;
      color: string;
    } = {
      thumbImage: null,
      image: null,
      color: '',
    };

    if (colorExists && options) {
      const color = options.color,
        mainImage = options.image.main,
        thumbnailImage = options.image.thumbnail;

      this.dispatchEvent(
        new CustomEvent('color-selected-event', {
          detail: { color },
          composed: true,
        }),
      );

      if (!mainImage) {
        return;
      }

      imageObject = {
        thumbImage: thumbnailImage,
        image: mainImage,
        color: color,
      };
    }

    this.dispatchEvent(
      new CustomEvent('color-selected', {
        composed: true,
        detail: imageObject,
      }),
    );
  }

  render() {
    if (!Array.isArray(this.colors) || this.colors.length === 0) return nothing;
    const indexOfSelectedColor = this.colors.findIndex(color => color.selected);
    const showMoreColors = this.colors.length > 6;
    const visibleItemsCount = this.getVisibleItemsCount();

    return html`
      <div
        role="region"
        aria-labelledby="accordion_${this.stepIndex}"
        ?hidden=${!this.open}
      >
        ${showMoreColors && !this.showAllColors
          ? html`
              <span class="sr-only" id="colors-description">
                ${t(
                  'content.configurator.chooseColorStep.moreColorsExplanation',
                )}
              </span>
            `
          : nothing}
        <ul
          id="colors_container_${this.colorItemIndex}"
          class="colors-wrapper ${classMap({
            'show-colors': this.showAllColors || indexOfSelectedColor > 4,
          })}"
          aria-label=${this.title}
          aria-describedby="colors-description"
        >
          ${this.colors.map((colorOption, index) => {
            const showMoreColorsButton =
              showMoreColors &&
              !this.showAllColors &&
              index === FgColorCollapsible.COLOR_DISPLAY_THRESHOLD;
            return this.showAllColors ||
              index <= FgColorCollapsible.COLOR_DISPLAY_THRESHOLD
              ? html`
                  <li
                    class="filter-options"
                    aria-disabled=${Boolean(colorOption.disabled)}
                  >
                    <div
                      class="filter-options-content ${classMap({
                        disabled: colorOption.disabled,
                      })}"
                    >
                      <span
                        aria-hidden="true"
                        class="checkmark ${classMap({
                          selected: colorOption.selected,
                        })}"
                      >
                        ${iconCheckmark}
                      </span>
                      <span
                        class="wrapper ${classMap({
                          selected: colorOption.selected,
                        })}"
                      >
                        ${colorOption?.image?.thumbnail
                          ? html`
                              <fg-image
                                height="96"
                                src="${colorOption?.image?.thumbnail}"
                                width="96"
                              ></fg-image>
                            `
                          : nothing}
                      </span>
                      <span class="sr-only">
                        ${colorOption.selected
                          ? t('actions.selectedColor')
                          : t('actions.chooseColor')}
                      </span>
                      <span class="color-name">${colorOption.color}</span>
                      ${colorOption.disabled ||
                      (!this.showAllColors &&
                        index === FgColorCollapsible.COLOR_DISPLAY_THRESHOLD &&
                        this.colors.length ===
                          FgColorCollapsible.COLOR_DISPLAY_THRESHOLD)
                        ? nothing
                        : html`
                            <button
                              class="filter-option-button"
                              ?disabled=${Boolean(
                                !this.showAllColors &&
                                index ===
                                  FgColorCollapsible.COLOR_DISPLAY_THRESHOLD &&
                                this.colors.length >
                                  FgColorCollapsible.COLOR_DISPLAY_THRESHOLD +
                                    1,
                              )}
                              name="color_${this.stepIndex}_${this
                                .colorItemIndex}"
                              id="${colorOption.color}"
                              @click="${() => {
                                const announcement = `${colorOption.color} ${t('labels.selected')}`;
                                this.dispatchEvent(
                                  new CustomEvent('handle-announcement', {
                                    composed: true,
                                    detail: {
                                      announcement,
                                    },
                                  }),
                                );
                                this.updateComponent(colorOption.queryString);
                                this.selectedColorImage(true, colorOption);
                              }}"
                              aria-label="${`${colorOption.color}${colorOption.selected ? ` ${t('labels.selected')}` : ''}, ${visibleItemsCount === 1 ? t('labels.colors_one', { count: visibleItemsCount }) : t('labels.colors_other', { count: visibleItemsCount })}`}"
                            >
                              <span class="sr-only">
                                ${`${colorOption.color}${colorOption.selected ? ` ${t('labels.selected')}` : ''}, ${visibleItemsCount === 1 ? t('labels.colors_one', { count: visibleItemsCount }) : t('labels.colors_other', { count: visibleItemsCount })}`}
                              </span>
                            </button>
                          `}
                    </div>
                    ${showMoreColorsButton
                      ? html`
                          <button
                            aria-label=${t('actions.moreColors') ?? nothing}
                            aria-controls="colors_container_${this
                              .colorItemIndex}"
                            aria-expanded="${this.showAllColors}"
                            class="more-colors-overlay"
                            @click=${() =>
                              (this.showAllColors = !this.showAllColors)}
                          >
                            <span class="more-colors-counter">
                              <span>
                                +${this.colors.length -
                                FgColorCollapsible.COLOR_DISPLAY_THRESHOLD}
                              </span>
                            </span>
                            <span
                              aria-hidden="true"
                              class="more-colors  ${classMap({
                                open: this.showAllColors,
                              })}"
                            >
                              ${t('actions.moreColors')} ${iconChevron}
                            </span>
                          </button>
                        `
                      : nothing}
                  </li>
                `
              : nothing;
          })}
        </ul>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    collapsibleOptionsStyles,
    css`
      :host {
        --fg-color-swatch-size: 80px;
      }

      fieldset {
        border: none;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        margin-top: 16px;
        padding: 16px;
      }

      span {
        font-size: var(---fg-font-size--xs, var(--fg-fallback-font-size--xs));
        margin-top: 4px;
      }

      .filter-options-content.disabled {
        cursor: not-allowed;
        opacity: 0.4;
      }

      .more-colors-overlay {
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        height: auto;
      }

      .more-colors {
        background-color: white;
        color: var(--fg-primary, var(--fg-fallback-primary));
        display: flex;
        align-items: center;
        column-gap: 4px;
        font-size: var(---fg-font-size--xs, var(--fg-fallback-font-size--xs));
        justify-content: center;
        margin-top: 0;
        overflow: hidden;
        text-align: center;
        text-decoration: underline;
        width: 100%;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        margin-top: 4px;
        white-space: nowrap;
      }

      .more-colors svg {
        height: 8px;
        transform: rotate(180deg);
        width: 8px;
      }

      .more-colors.open svg {
        transform: rotate(0deg);
      }

      .more-colors-counter {
        display: grid;
        height: var(--fg-color-swatch-size);
        margin: 0;
        place-items: center;
        width: var(--fg-color-swatch-size);
      }

      .more-colors-counter::after {
        background-color: white;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        content: '';
        max-height: var(--fg-color-swatch-size);
        height: 100%;
        max-width: var(--fg-color-swatch-size);
        left: 0;
        opacity: 0.6;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 1;
      }

      .more-colors-counter span {
        color: var(--fg-gray-800, var(--fg-fallback-gray-800));
        font-size: var(--fg-font-size--xl, var(--fg-fallback-font-size--xl));
        font-weight: 700;
        margin-top: 0;
        position: relative;
        z-index: 2;
      }

      .filter-options {
        position: relative;
        max-width: var(--fg-color-swatch-size);
        width: 100%;
      }

      .colors-wrapper {
        display: grid;
        column-gap: 8px;
        grid-auto-rows: 0;
        grid-template-columns: repeat(
          3,
          minmax(0, var(--fg-color-swatch-size))
        );
        grid-template-rows: repeat(2, auto);
        margin-left: -4px;
        padding: 16px 4px 4px;
        overflow: hidden;
      }

      .colors-wrapper.show-colors {
        grid-auto-rows: auto;
        margin-bottom: 0;
        overflow: visible;
        row-gap: 8px;
      }

      .wrapper {
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        display: grid;
        height: var(--fg-color-swatch-size);
        margin-top: 0;
        place-items: center;
        position: relative;
        width: var(--fg-color-swatch-size);
      }

      .wrapper.selected::after {
        background-color: color-mix(
          in srgb,
          var(--fg-message-success, var(--fg-fallback-message-success)),
          transparent 95%
        );
        filter: alpha(opacity=5);
        border: 2px solid var(--fg-success, var(--fg-fallback-success));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        content: '';
        display: block;
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }

      .wrapper.disabled::after {
        background-color: var(--fg-success, var(--fg-fallback-success));
        content: '';
        display: block;
        height: 100%;
        left: 0;
        opacity: 0.05;
        position: absolute;
        top: 0;
        width: 100%;
      }

      .wrapper fg-image::part(image) {
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        height: calc(var(--fg-color-swatch-size) - 2px);
        object-fit: contain;
        width: calc(var(--fg-color-swatch-size) - 2px);
      }

      .wrapper,
      .wrapper fg-image::part(image) {
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
      }

      .color-name {
        display: inline-block;
      }

      .color-name:first-letter {
        text-transform: uppercase;
      }

      .wrapper.selected ~ .color-name {
        font-weight: 700;
      }

      .filter-options-content {
        display: block;
        text-align: center;

        &:hover {
          .wrapper {
            border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
          }

          .color-name {
            text-decoration: underline;
          }
        }
      }

      .filter-option-button {
        appearance: none;
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        position: absolute;
        top: 0;
        width: 100%;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          --fg-color-swatch-size: 96px;
        }
        .colors-wrapper {
          grid-template-columns: repeat(
            6,
            minmax(0, var(--fg-color-swatch-size))
          );
          grid-template-rows: auto;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) and (max-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        :host {
          --fg-color-swatch-size: 80px;
        }
        .wrapper fg-image::part(image) {
          height: calc(var(--fg-color-swatch-size) - 4px);
          width: calc(var(--fg-color-swatch-size) - 4px);
        }
      }
    `,
  ];
}
